/**
 * Error Modal Utility
 * Reusable error modal for displaying error details to users
 */

/**
 * Get user IP address
 * @returns {Promise<string>} User IP address or 'Unable to fetch'
 */
async function getUserIP() {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || 'Unable to fetch';
  } catch (e) {
    return 'Unable to fetch';
  }
}

/**
 * Show error modal with details
 * @param {Error} error - The error object
 * @param {string} endpoint - The API endpoint that failed
 * @param {Object} options - Additional options
 * @param {string} options.title - Modal title (default: "Registration Error")
 * @param {string} options.message - Error message (default: "An unexpected error occurred. Please try again.")
 * @param {Object} options.additionalInfo - Additional debug information to include
 */
async function showErrorModal(error, endpoint, options = {}) {
  const {
    title = 'Registration Error',
    message = 'An unexpected error occurred. Please try again.',
    additionalInfo = {}
  } = options;
  const userIP = await getUserIP();
  
  const debugInfo = {
    errorType: error?.name || 'Unknown',
    errorMessage: error?.message || '',
    endpoint: endpoint || 'Unknown',
    online: navigator.onLine,
    browser: navigator.userAgent,
    platform: navigator.platform,
    ip: userIP,
    time: new Date().toISOString(),
    ...additionalInfo,
  };
  
  const modal = document.createElement('div');
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  `;
  
  const content = document.createElement('div');
  content.style.cssText = `
    background: white;
    border-radius: 8px;
    padding: 30px;
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  `;
  
  let detailsVisible = false;
  const detailsSection = document.createElement('div');
  detailsSection.style.cssText = `
    display: none;
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
  `;
  
  detailsSection.innerHTML = `
    <h3 style="margin-top: 0; margin-bottom: 15px; color: #333; font-size: 18px;">Error Details</h3>
    <div style="background: #f5f5f5; padding: 15px; border-radius: 4px; margin-bottom: 15px; font-family: monospace; font-size: 12px; white-space: pre-wrap; word-break: break-all; max-height: 300px; overflow-y: auto; line-height: 1.6;">
${JSON.stringify(debugInfo, null, 2)}
    </div>
    <button id="copyErrorBtn" style="padding: 10px 20px; background: #2563eb; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; margin-right: 10px;">Copy Error Details</button>
  `;
  
  content.innerHTML = `
    <h2 style="margin-top: 0; color: #DC2626; font-size: 24px; margin-bottom: 15px;">${title}</h2>
    <p style="margin-bottom: 20px; color: #333; line-height: 1.6;">${message}</p>
    <button id="viewDetailsBtn" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px; margin-right: 10px;">View Details</button>
    <button id="closeErrorBtn" style="padding: 10px 20px; background: #6b7280; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;">Close</button>
  `;
  
  content.appendChild(detailsSection);
  modal.appendChild(content);
  document.body.appendChild(modal);
  
  const viewDetailsBtn = content.querySelector('#viewDetailsBtn');
  const closeBtn = content.querySelector('#closeErrorBtn');
  const copyBtn = detailsSection.querySelector('#copyErrorBtn');
  
  viewDetailsBtn.addEventListener('click', function() {
    if (!detailsVisible) {
      detailsSection.style.display = 'block';
      viewDetailsBtn.textContent = 'Hide Details';
      detailsVisible = true;
    } else {
      detailsSection.style.display = 'none';
      viewDetailsBtn.textContent = 'View Details';
      detailsVisible = false;
    }
  });
  
  copyBtn.addEventListener('click', function() {
    const errorText = JSON.stringify(debugInfo, null, 2);
    navigator.clipboard.writeText(errorText).then(() => {
      copyBtn.textContent = 'Copied!';
      setTimeout(() => {
        copyBtn.textContent = 'Copy Error Details';
      }, 2000);
    }).catch(() => {
      // Fallback: select text
      const textArea = document.createElement('textarea');
      textArea.value = errorText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        copyBtn.textContent = 'Copied!';
        setTimeout(() => {
          copyBtn.textContent = 'Copy Error Details';
        }, 2000);
      } catch (err) {
        alert('Failed to copy. Please select and copy manually.');
      }
      document.body.removeChild(textArea);
    });
  });
  
  closeBtn.addEventListener('click', function() {
    document.body.removeChild(modal);
  });
  
  modal.addEventListener('click', function(e) {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
}

// Export to window for global access
window.showErrorModal = showErrorModal;
window.getUserIP = getUserIP;

